(ns manage.product-selector-page.sections.conditions
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Paper$default" :as Paper]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/TextField$default" :as TextField]
            ["@mui/material/Typography$default" :as Typography]
            ["react-hook-form" :as rhf]
            [cljs-bean.core :refer [->clj ->js]]
            [clojure.string :as str]
            [common.form :as form]
            [common.util :as util]
            [common.widgets.datepicker :as datepicker]
            [common.widgets.dropdown :refer [dropdown]]
            [common.widgets.tag-input :refer [tag-input]]
            [manage.product-selector-page.hooks :as hooks]
            [manage.product-selector-page.widgets :as widgets]
            [mate.core :as mc]
            [reagent.core :as r]
            [uix.core :refer [$ defhook defui]]))

(defhook use-filter-clause-field-options []
  (let [field-options (hooks/use-field-options)]
    (filter #(contains? hooks/operators (:type %)) field-options)))

(defhook use-filter-clause-operator-options [field]
  (let [field-options (hooks/use-field-options)]
    (->> field-options
         (some #(when (= (:name %) field) (:type %)))
         hooks/operators)))

(defui filter-clause-field
  [{:keys [control field-name]}]
  (let [field-options (use-filter-clause-field-options)]
    ($ rhf/Controller
       {:name (str field-name ".field")
        :control control
        :render (fn [^js props]
                  (let [{{:keys [error]} :fieldState
                         {:keys [onChange value]} :field} (->clj props)
                        {:keys [setValue]} (form/use-form-context)]
                    (r/as-element
                     [dropdown
                      {:error (boolean error)
                       :text-field-props {:helperText (:message error)}
                       :on-change (fn [v]
                                    (let [filter (some #(when (= (:field %) v) %) field-options)]
                                      (onChange (:field filter))
                                      (setValue (str field-name ".kind") (:kind filter))
                                      (setValue (str field-name ".type") (:type filter))
                                      (setValue (str field-name ".label") (:label filter))
                                      (setValue (str field-name ".operator") nil)))
                       :value value
                       :options field-options}])))})))

(defui filter-clause-operator
  [{:keys [control field-name]}]
  ($ rhf/Controller
     {:name (str field-name ".operator")
      :control control
      :render (fn [^js props]
                (let [{{:keys [error]} :fieldState
                       {:keys [onChange value]} :field} (->clj props)
                      {:keys [setValue]} (form/use-form-context)
                      filter-clause (rhf/useWatch #js {:name field-name :control control})
                      operator-options (use-filter-clause-operator-options (.-field filter-clause))]
                  (r/as-element
                   [dropdown
                    {:error (boolean error)
                     :text-field-props {:helperText (:message error)}
                     :on-change (fn [operator]
                                  (let [filter-value (.-value filter-clause)]
                                    (onChange operator)
                                    (cond
                                      (and (string? filter-value)
                                           (#{"terms" "terms-not"} operator))
                                      (setValue (str field-name ".value") (if (str/blank? filter-value)
                                                                            []
                                                                            (str/split filter-value #"\ ")))

                                      (and (or (coll? filter-value) (array? filter-value))
                                           (#{"term" "term-not"} operator))
                                      (setValue (str field-name ".value") (str/join " " filter-value)))))
                     :value value
                     :options (or operator-options [])
                     :disabled? (nil? (.-field filter-clause))}])))}))

(defui filter-clause-value-input-number
  [{:keys [value on-change error _operator]}]
  ($ TextField
     {:value value
      :onChange #(on-change (-> % .-target .-value))
      :inputProps #js {:type "number"}
      :error (boolean error)
      :helperText (:message error)}))

(defui filter-clause-value-input-boolean
  [{:keys [value on-change error _operator]}]
  (r/as-element
   [dropdown
    {:value value
     :on-change on-change
     :error (boolean error)
     :text-field-props {:helperText (:message error)}
     :options [{:value true :label "True"}
               {:value false :label "False"}]}]))

(defui filter-clause-value-input-date-time
  [{:keys [value on-change error _operator]}]
  ($ datepicker/mixed-date-picker
     {:value value
      :value-fn #(.format % "yyyy-MM-DD")
      :on-change on-change
      :error (boolean error)
      :helperText (:message error)
      :sx #js {:width "100%"}}))

(defui filter-clause-value-input-default
  [{:keys [value on-change error operator]}]
  (if (#{"terms" "terms-not"} operator)
    ($ tag-input
       {:value value
        :on-change on-change
        :error (boolean error)
        :text-field-props {:helperText (:message error)}
        :multi? true
        :clearable? true
        :options value})
    ($ TextField
       {:value value
        :onChange #(on-change (-> % .-target .-value))
        :error (boolean error)
        :helperText (:message error)})))

(defn type->input-component [type]
  (case type
    "number" filter-clause-value-input-number
    "boolean" filter-clause-value-input-boolean
    "date-time" filter-clause-value-input-date-time
    filter-clause-value-input-default))

(defui filter-clause-value
  [{:keys [control field-name]}]
  ($ rhf/Controller
     {:name (str field-name ".value")
      :control control
      :render (fn [^js props]
                (let [{{:keys [error]} :fieldState
                       {:keys [onChange value]} :field} (->clj props)
                      filter-clause (rhf/useWatch #js {:name field-name :control control})
                      input-component (type->input-component (.-type filter-clause))]
                  ($ input-component
                     {:value value
                      :on-change #(onChange %)
                      :error error
                      :operator (.-operator filter-clause)})))}))

(defui filter-clause
  [{:keys [on-delete idx] :& props}]
  ($ Paper
     {:elevation 0
      :component Stack
      :gap 1
      :sx #js {:px 1.5
               :py 1}}
     ($ Stack
        {:direction "row"
         :justifyContent "space-between"}
        ($ Typography
           {:sx #js {:textTransform "uppercase"
                     :fontWeight 600
                     :color "grey.20"}}
           (if (= idx 0) "if" "and"))
        ($ IconButton
           {:onClick on-delete
            :sx #js {:alignSelf "flex-end"
                     :p 0}}
           ($ Icon "delete")))
     ($ filter-clause-field props)
     ($ filter-clause-operator props)
     ($ filter-clause-value props)))

(defui edit []
  (let [{:keys [control]} (form/use-form-context)
        field-name "query.filter"
        {:keys [fields append remove]} (form/use-field-array #js {:control control
                                                                  :name field-name})
        product-store-fields (hooks/use-product-store-fields)]
    ($ Stack
       {:gap 2}
       (for [[idx field] (mc/seq-indexed fields)]
         ($ filter-clause
            {:key (.-id field)
             :idx idx
             :field-name (str field-name "[" idx "]")
             :control control
             :on-delete #(remove idx)}))
       ($ Button
          {:variant "text"
           :startIcon ($ Icon "add")
           :onClick #(append (->js (hooks/initial-filter-clause product-store-fields)))
           :data-testid "Create a new condition"
           :sx #js {:alignSelf "flex-start"}}
          "Add condition"))))

(defui read-filter-clause [{:keys [filter]}]
  (let [{:keys [label operator value type]} filter
        operator-map (->> (get hooks/operators type)
                          (mc/index-by :value))]
    ($ Stack
       {:direction "row"
        :gap 1
        :divider ($ :span "—")}
       ($ Typography {:fontWeight 600} label)
       ($ Typography {:fontWeight 600} (:label (get operator-map operator)))
       ($ Typography {:noWrap true}
          (cond
            (coll? value) (str/join ", " value)

            (and (= type "date-time") (util/valid-elasticsearch-date-string? value))
            (datepicker/external-string->readable value)

            (and (= type "date-time") (util/valid-date-string? value "YYYY-MM-DD"))
            (util/format-iso-string value "L")

            :else (str value))))))

(defui read []
  (let [{:keys [control]} (form/use-form-context)
        filters (rhf/useWatch #js {:name "query.filter" :control control})]
    ($ Stack {:gap 2}
       (for [[idx filter] (mc/seq-indexed filters)]
         ($ read-filter-clause {:key idx :filter (->clj filter)}))
       (when (empty? filters)
         ($ widgets/empty-section-message {:step-label "conditions"})))))

(defui conditions []
  (let [{:keys [mode]} (form/use-form-context)]
    ($ widgets/section-card
       {:title "Conditions"
        :tooltip "Set the rules that products must meet to be included in your results."}
       (if (= mode :read)
         ($ read)
         ($ edit)))))
